
import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiSettings, FiCheckSquare } from 'react-icons/fi';
import api from '../services/api';

const Settings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [allowEdit, setAllowEdit] = useState(true);
    const [allowAddAfterStart, setAllowAddAfterStart] = useState(false);
    const [allowPostTrainingCheckin, setAllowPostTrainingCheckin] = useState(false);
    const [feedback, setFeedback] = useState({ type: '', msg: '' });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await api.get('/settings');
            setAllowEdit(response.data.allow_edit_training);
            setAllowAddAfterStart(response.data.allow_add_participant_after_start);
            setAllowPostTrainingCheckin(response.data.allow_post_training_checkin);
        } catch (err) {
            console.error('Erro ao carregar configurações', err);
            setFeedback({ type: 'danger', msg: 'Erro ao carregar configurações.' });
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = async (name, checked) => {
        // Update local state immediately
        if (name === 'allow_edit_training') setAllowEdit(checked);
        if (name === 'allow_add_participant_after_start') setAllowAddAfterStart(checked);
        if (name === 'allow_post_training_checkin') setAllowPostTrainingCheckin(checked);
        
        setSaving(true);
        setFeedback({ type: '', msg: '' });

        try {
            await api.post('/settings', { [name]: checked });
            setFeedback({ type: 'success', msg: 'Configuração salva com sucesso!' });
        } catch (err) {
            console.error('Erro ao salvar configuração', err);
            setFeedback({ type: 'danger', msg: 'Erro ao salvar configuração. Verifique se você tem permissão.' });
            // Revert on error
            if (name === 'allow_edit_training') setAllowEdit(!checked);
            if (name === 'allow_add_participant_after_start') setAllowAddAfterStart(!checked);
            if (name === 'allow_post_training_checkin') setAllowPostTrainingCheckin(!checked);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container className="mt-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <div className="d-flex align-items-center mb-4">
                <Button 
                    variant="link" 
                    as={Link} 
                    to="/dashboard" 
                    className="text-decoration-none p-0 mb-3 text-muted"
                >
                    <FiArrowLeft className="me-1" /> Voltar ao Dashboard
                </Button>
            </div>
            <div className="d-flex align-items-center mb-4">                
                <h2 className="fw-bold mb-0" style={{ color: '#006064' }}>Configurações do Sistema</h2>
            </div>

            {feedback.msg && (
                <Alert variant={feedback.type} dismissible onClose={() => setFeedback({ type: '', msg: '' })}>
                    {feedback.msg}
                </Alert>
            )}

            <Card className="shadow-sm border-0" style={{ backgroundColor: '#F5F7FA' }}>
                <Card.Body className="p-4">
                    <h5 className="fw-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>Geral</h5>
                    
                    <Row>
                        <Col md={12}>
                            <Form.Group className="d-flex align-items-center bg-white p-3 rounded shadow-sm">
                                <div className="me-3 text-primary">
                                    <FiCheckSquare size={24} />
                                </div>
                                <div className="flex-grow-1">
                                    <Form.Check 
                                        type="switch"
                                        id="allow-edit-switch"
                                        label="Permitir editar treinamento"
                                        checked={allowEdit}
                                        onChange={(e) => handleCheckboxChange('allow_edit_training', e.target.checked)}
                                        disabled={saving}
                                        className="fw-bold text-dark fs-5"
                                        style={{ fontFamily: 'Inter, sans-serif' }}
                                    />
                                    <Form.Text className="text-muted">
                                        Habilita ou desabilita a edição de treinamentos para todos os usuários autorizados.
                                    </Form.Text>
                                </div>
                                {saving && <Spinner animation="border" size="sm" variant="primary" />}
                            </Form.Group>
                        </Col>
                        
                        <Col md={12} className="mt-3">
                            <Form.Group className="d-flex align-items-center bg-white p-3 rounded shadow-sm">
                                <div className="me-3 text-primary">
                                    <FiCheckSquare size={24} />
                                </div>
                                <div className="flex-grow-1">
                                    <Form.Check 
                                        type="switch"
                                        id="allow-add-switch"
                                        label="Adicionar Participante após início de treinamento"
                                        checked={allowAddAfterStart}
                                        onChange={(e) => handleCheckboxChange('allow_add_participant_after_start', e.target.checked)}
                                        disabled={saving}
                                        className="fw-bold text-dark fs-5"
                                        style={{ fontFamily: 'Inter, sans-serif' }}
                                    />
                                    <Form.Text className="text-muted">
                                        Permite adicionar novos participantes a um treinamento mesmo após a data de início.
                                    </Form.Text>
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={12} className="mt-3">
                            <Form.Group className="d-flex align-items-center bg-white p-3 rounded shadow-sm">
                                <div className="me-3 text-primary">
                                    <FiCheckSquare size={24} />
                                </div>
                                <div className="flex-grow-1">
                                    <Form.Check 
                                        type="switch"
                                        id="allow-post-checkin-switch"
                                        label="Permitir check-in após término do treinamento"
                                        checked={allowPostTrainingCheckin}
                                        onChange={(e) => handleCheckboxChange('allow_post_training_checkin', e.target.checked)}
                                        disabled={saving}
                                        className="fw-bold text-dark fs-5"
                                        style={{ fontFamily: 'Inter, sans-serif' }}
                                    />
                                    <Form.Text className="text-muted">
                                        Quando ativado, permite check-in após horário de término do treinamento, ignorando tolerâncias configuradas.
                                    </Form.Text>
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default Settings;
